import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Shield,
  Settings,
  LogIn
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const screenshotIcons = [
  LayoutDashboard, Settings, LogIn, Users, Shield,
  LayoutDashboard, Users, Shield, Settings
];

const screenshotImages = [
  '/Selection_240.png',
  '/Selection_243.png',
  '/Selection_244.png',
  '/Selection_245.png',
  '/Selection_246.png',
  '/Selection_247.png',
  '/Selection_241.png',
  '/Selection_239.png',
  '/Selection_238.png'
];

const screenshotIds = [
  'dashboard', 'instances', 'subscribe', 'sharing', 'licenses',
  'signatures', 'customer', 'cos', 'cos-features'
];

const ScreenshotShowcase = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useTranslation('zimbra-admin-panel');
  const items = t('screenshots.items', { returnObjects: true }) as Array<{ name: string; description: string }>;

  const active = items[activeIndex];
  const activeImage = screenshotImages[activeIndex];

  return (
    <section className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm font-semibold mb-4 border border-blue-100 dark:border-blue-800">
            {t('screenshots.badge')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('screenshots.title')}
          </h2>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            {t('screenshots.description')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0"
          >
            {items.map((s, idx) => {
              const Icon = screenshotIcons[idx] ?? LayoutDashboard;
              return (
                <button
                  key={screenshotIds[idx]}
                  onClick={() => setActiveIndex(idx)}
                  className={`flex-shrink-0 lg:flex-shrink text-left flex items-start gap-3 px-4 py-4 rounded-xl border transition-all duration-200 ${
                    activeIndex === idx
                      ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200 dark:shadow-blue-900/30'
                      : 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/10'
                  }`}
                >
                  <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${activeIndex === idx ? 'text-white' : 'text-blue-500 dark:text-blue-400'}`} />
                  <div className="min-w-0">
                    <div className={`font-semibold text-sm ${activeIndex === idx ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                      {s.name}
                    </div>
                    <div className={`text-xs mt-0.5 leading-relaxed hidden lg:block ${activeIndex === idx ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                      {s.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800">
              <div className="flex items-center gap-2 px-4 py-3 bg-gray-200 dark:bg-slate-700 border-b border-gray-300 dark:border-slate-600">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <div className="flex-1 mx-4 bg-white dark:bg-slate-600 rounded-md px-3 py-1 text-xs text-gray-500 dark:text-gray-300 font-mono">
                  zcspanel.example.com
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  transition={{ duration: 0.25 }}
                >
                  <img
                    src={activeImage}
                    alt={active?.name ?? ''}
                    className="w-full h-auto block"
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            <AnimatePresence mode="wait">
              <motion.p
                key={activeIndex}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-4 text-gray-600 dark:text-gray-400 text-sm text-center lg:hidden"
              >
                {active?.description}
              </motion.p>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ScreenshotShowcase;
